import React from 'react'
import classNames from 'classnames'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { LivestreamThumbnailPill } from '@/molecules/LivestreamThumbnailPill'
import { LivestreamTitle } from '@/page/LivestreamLandscapeRowCollection/LivestreamTitle'
import { useTranslate } from '@/utils/translate/translate-client'

interface CurrentLivestreamThumbnailProps {
  live?: boolean
  guid: string
  bannerCloudinaryPath: string
  startAt: string
  isEnded: boolean
  className?: string
  animationClassName?: string
  name?: string
  unavailableReason?: string | null
  thumbnailClassName?: string
  includeHoverScale?: boolean
}

export const LivestreamThumbnail: React.FC<CurrentLivestreamThumbnailProps> = ({
  live = false,
  guid,
  bannerCloudinaryPath,
  startAt,
  isEnded,
  className,
  animationClassName = 'duration-[400ms] ease-in-out',
  name,
  unavailableReason,
  thumbnailClassName,
  includeHoverScale = false,
}) => {
  const { t } = useTranslate('watch')
  if (typeof guid === 'undefined' || guid === null || !bannerCloudinaryPath) return null
  const href = `${paths.livestream.index}/${guid}`

  return (
    <InternalLink href={href} className="group cursor-pointer">
      <div className="flex flex-row items-start gap-4">
        <div className="shrink-0">
          <div
            className={classNames(
              'relative overflow-hidden rounded-lg transition-transform duration-[400ms] ease-in-out',
              { 'border-red-400 border-4': live },
              { 'md:group-hover:scale-[1.05]': includeHoverScale },
              className,
            )}
          >
            <Image
              alt={`${name} Livestream Thumbnail`}
              src={bannerCloudinaryPath}
              className={classNames(
                'h-full max-h-[101px] w-full max-w-[180px] md:max-h-[144px] md:max-w-[256px] lg:max-h-[169px] lg:max-w-[300px]',
                thumbnailClassName,
              )}
              width={300}
              height={169}
            />
            <div className="absolute inset-0">
              <div
                className={classNames(
                  'flex h-full w-full items-center justify-center opacity-0 transition',
                  animationClassName,
                )}
              >
                <div className="mx-auto h-fit w-fit !opacity-100">
                  <PlayCircleIcon />
                </div>
              </div>
              <div className="absolute bottom-2 right-2">
                <LivestreamThumbnailPill
                  live={Boolean(live)}
                  isEnded={isEnded}
                  startAt={startAt}
                  isGuildOnly={unavailableReason === 'PRERELEASE'}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:gap-x-0">
          <div className="flex flex-col gap-[6px]">
            <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
              {t('livestream', 'Livestream')}
            </CaptionSM>
            <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
              {name}
            </CaptionSM>
            <LivestreamTitle
              live={live}
              isEnded={isEnded}
              startAt={startAt}
              className="!photon-caption-sm lg:!photon-caption-lg !text-core-gray-500"
            />
          </div>
          <div className="lg:hidden">
            <CaretRightIcon size={24} color="white" />
          </div>
        </div>
      </div>
    </InternalLink>
  )
}
